import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { SupabaseClient } from '@supabase/supabase-js';
import { buttonVariants } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { getSupabaseBrowserClient } from '@/lib/supabase';

const PKCE_CLAIM_PREFIX = 'adoracare_oauth_pkce_';

function pkceClaimKey(code: string): string {
  return `${PKCE_CLAIM_PREFIX}${code}`;
}

async function redirectHomeIfSession(
  supabase: SupabaseClient,
  navigate: ReturnType<typeof useNavigate>,
): Promise<boolean> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (session) {
    navigate('/home', { replace: true });
    return true;
  }
  return false;
}

export default function AuthCallbackPage() {
  const navigate = useNavigate();
  const [message, setMessage] = useState('Completing sign-in…');
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  useEffect(() => {
    const supabase = getSupabaseBrowserClient();
    if (!supabase) {
      setMessage('Supabase is not configured for this build.');
      return;
    }

    const href = window.location.href;
    let cancelled = false;

    let pollId: ReturnType<typeof setInterval> | undefined;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    const clearTimers = () => {
      if (pollId !== undefined) clearInterval(pollId);
      if (timeoutId !== undefined) clearTimeout(timeoutId);
    };

    void (async () => {
      if (await redirectHomeIfSession(supabase, navigateRef.current)) return;

      let code: string | null = null;
      try {
        code = new URL(href).searchParams.get('code');
      } catch {
        code = null;
      }

      if (!code) {
        setMessage('No authorization code in this URL. Start sign-in from the login page.');
        return;
      }

      const key = pkceClaimKey(code);
      const claim = sessionStorage.getItem(key);

      if (claim === 'done') {
        if (!(await redirectHomeIfSession(supabase, navigateRef.current))) {
          setMessage('Sign-in state is stale. Please try logging in again.');
        }
        return;
      }

      if (claim === 'exchanging') {
        pollId = setInterval(() => {
          void (async () => {
            if (cancelled) return;
            if (await redirectHomeIfSession(supabase, navigateRef.current)) {
              clearTimers();
            }
          })();
        }, 120);
        timeoutId = setTimeout(() => {
          clearTimers();
          if (!cancelled) {
            setMessage('Sign-in is taking too long. Close this tab and try again from the login page.');
          }
        }, 20_000);
        return;
      }

      sessionStorage.setItem(key, 'exchanging');

      const { error } = await supabase.auth.exchangeCodeForSession(href);
      if (cancelled) return;

      if (error) {
        sessionStorage.removeItem(key);
        setMessage(error.message);
        return;
      }

      sessionStorage.setItem(key, 'done');
      navigateRef.current('/home', { replace: true });
    })();

    return () => {
      cancelled = true;
      clearTimers();
    };
  }, []);

  return (
    <main className="mx-auto flex min-h-screen max-w-lg flex-col justify-center px-4 py-10">
      <Card>
        <CardHeader>
          <CardTitle>Authentication</CardTitle>
          <CardDescription>{message}</CardDescription>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          If you are not redirected automatically, confirm the callback URL is
          allow-listed in the Supabase dashboard.
        </CardContent>
        <CardFooter>
          <Link to="/home" className={buttonVariants({ variant: 'outline' })}>
            Go to home
          </Link>
        </CardFooter>
      </Card>
    </main>
  );
}
